import { InputBase } from '@mui/material';
import React, {
  CSSProperties,
  forwardRef,
  KeyboardEvent,
  useImperativeHandle,
  useRef,
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import SvgImage from '@/components/Module/SvgImage';
import { InputLayout } from '@/components/CustomInput/styles';
import { SearchData } from '@/types/api';
import { StyledSVGButton } from '@/styles/GlobalStyles';

type P = {
  isFocus?: boolean;
  placeHolder: string;
  handleFocus?(): void;
  style?: CSSProperties;
  onSubmit: ({ keyword }: Pick<SearchData, 'keyword'>) => void;
  handleKeyUp: (event: KeyboardEvent<HTMLInputElement>) => void;
};

const NewCustomInput = forwardRef<HTMLInputElement, P>(
  (
    { isFocus, style, placeHolder, handleFocus, onSubmit, handleKeyUp },
    ref,
  ) => {
    const { control, watch, reset } = useFormContext<{
      keyword: string;
    }>();
    const searchString = watch('keyword');

    const inputRef = useRef<HTMLInputElement | null>(null);

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    return (
      <InputLayout style={style} isFocus={isFocus}>
        <Controller
          control={control}
          rules={{
            required: '키워드를 입력해 주세요.',
            minLength: {
              value: 2,
              message: '키워드는 최소 2자 이상 입력해야 합니다.',
            },
          }}
          render={({ field }) => (
            <>
              <InputBase
                {...field}
                inputRef={(el) => {
                  field.ref(el); // RHF 내부 관리용 ref
                  inputRef.current = el; // 🔧 내부 ref에 저장
                }}
                onKeyUp={handleKeyUp}
                onFocus={handleFocus}
                autoComplete={'off'}
                sx={{
                  flex: '1',
                  fontSize: '14px',
                  // 테마 고정 높이(44px)는 보더 있는 래퍼 내부(42px)보다 커서 래퍼 크기에 맞춤. 셀렉터 형태여야 테마 값을 덮어 쓸 수 있음.
                  '&.MuiInputBase-root': { height: '100%' },
                  // 내부 input 도 테마 세로 패딩(12px) 때문에 44px 로 고정돼 래퍼 밖으로 넘침 — 래퍼 높이에 맞춰 세로 중앙 정렬
                  '& .MuiInputBase-input': {
                    height: '100%',
                    boxSizing: 'border-box',
                    paddingTop: 0,
                    paddingBottom: 0,
                  },
                }}
                placeholder={placeHolder}
              />
            </>
          )}
          name={'keyword'}
        />
        <div style={{ display: 'flex', gap: '12px' }}>
          {searchString && (
            <StyledSVGButton
              onClick={() => {
                reset();
                onSubmit({ keyword: '' });
              }}
            >
              <SvgImage name={'delete'} />
            </StyledSVGButton>
          )}
          <StyledSVGButton onClick={() => onSubmit({ keyword: searchString })}>
            <SvgImage name={'search'} />
          </StyledSVGButton>
        </div>
      </InputLayout>
    );
  },
);

NewCustomInput.displayName = 'NewCustomInput';

export default NewCustomInput;
